import React, { useState } from "react";
import { useSidebarContext } from "../context/SidebarContext";
import CustomInput from "./CustomInput";
import { useForm } from "react-hook-form";

const DeletionReasonModal = ({
  isOpen,
  onClose,
  onDelete,
  itemName,
  loading,
  title = "Confirm Deletion",
  description = "Please provide a reason for deletion:",
  deletionReason,
  setDeletionReason,
}) => {
  const { toggleSidebar } = useSidebarContext();
  const { register, formState: { errors }, watch } = useForm({
    defaultValues: {
      reason: deletionReason || ""
    }
  });
  
  // State to track confirmation step
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Update parent state when reason changes
  React.useEffect(() => {
    const subscription = watch((value) => {
      if (value.reason !== undefined) {
        setDeletionReason(value.reason);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setDeletionReason]);
  
  if (!isOpen) return null;

  // Handle the first delete click
  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  // Handle the final confirmation
  const handleConfirmDelete = (e) => {
    // Create a synthetic event if none is provided
    const event = e || {
      preventDefault: () => {},
      stopPropagation: () => {}
    };
    onDelete(event);
  };

  // Handle cancel in confirmation step
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div
      className={`fixed inset-0 z-[60] mt-[150px] flex items-center justify-center overflow-y-auto ${
        toggleSidebar ? "ml-[335px]" : "ml-[80px]"
      }`}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-0 transition-opacity"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (showConfirmation) {
            handleCancelConfirmation();
          } else {
            onClose(e);
          }
        }}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
        <div className="bg-[#0a2558] p-4">
          <h3 className="text-center text-xl font-medium text-[#F7CA41]">
            {title}
          </h3>
        </div>

        <div className="p-6">
          {!showConfirmation ? (
            // Step 1: Reason input
            <>
              <p className="mb-4 text-center text-gray-700">{description}</p>
              
              {/* Deletion Reason Input */}
              <div className="mb-4">
                <CustomInput
                  name="reason"
                  placeholder="Reason for deletion"
                  register={register("reason", { required: "Reason is required" })}
                  errors={errors}
                />
              </div>

              {itemName && (
                <p className="mb-4 text-center font-medium text-gray-900">
                  {itemName}
                </p>
              )}

              <div className="flex justify-between gap-4 border-t px-4 pb-4">
                <button
                  onClick={onClose}
                  className="w-full cursor-pointer rounded-[4px] border border-[#0a2558] bg-white px-4 py-4 text-xl text-[#0a2558] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteClick}
                  className="w-full cursor-pointer rounded-[4px] bg-dark-burgundy px-4 py-4 text-xl text-white hover:bg-dark-burgundy/90 disabled:opacity-50"
                  disabled={loading || !watch("reason")?.trim()}
                >
                  Delete
                </button>
              </div>
            </>
          ) : (
            // Step 2: Confirmation
            <>
              <p className="mb-4 text-center text-gray-700">Are you sure you want to delete?</p>
              
              {itemName && (
                <p className="mb-4 text-center font-medium text-gray-900">
                  {itemName}
                </p>
              )}

              <div className="flex justify-between gap-4 border-t px-4 pb-4">
                <button
                  onClick={handleCancelConfirmation}
                  className="w-full cursor-pointer rounded-[4px] border border-[#0a2558] bg-white px-4 py-4 text-xl text-[#0a2558] hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="w-full cursor-pointer rounded-[4px] bg-dark-burgundy px-4 py-4 text-xl text-white hover:bg-dark-burgundy/90 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeletionReasonModal;